import React, { useState, useEffect, useRef } from 'react';
import {
  Card, CardContent, CardHeader, Typography, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Box,
  Collapse, IconButton, Grid, Chip, Select, MenuItem, TextField, Alert, Button
} from '@mui/material';
import {
  ExpandMore, ExpandLess, LocationOn, TableChart, Visibility
} from '@mui/icons-material';

const FoundationLayoutViewer = ({ foundation, onLayoutChange, isEditable = false }) => {
  const [expanded, setExpanded] = useState(false);
  const [pileData, setPileData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [nextPileId, setNextPileId] = useState(1);
  // Track input values separately to allow intermediate states like "-" or empty string
  const [inputValues, setInputValues] = useState({});

  // Safely access foundation properties with fallbacks
  const piles = foundation?.layout || [];
  const dimensions = foundation?.dimensions || { L: 0, W: 0, H: 0 };
  const numPiles = piles.length;

  // Minimum edge distance (pile outer edge to foundation edge)
  const MIN_EDGE_DISTANCE = 0.1; // 100mm
  const MIN_PILE_SEPARATION = 0.5; // 500mm minimum center-to-center distance between piles
  const PILE_SIZE = foundation?.pile_size ? foundation.pile_size / 1000 : 0.3; // Convert mm to m, default 300mm
  const PILE_RADIUS = PILE_SIZE / 2; // Half the pile size for edge distance calculation

  // Initialize pile data when foundation code changes (not when layout changes)
  // Use a ref to track if we've initialized to prevent resetting during edits
  const initializedRef = React.useRef(false);
  const lastFoundationCodeRef = React.useRef(foundation?.code);

  useEffect(() => {
    // Only reset if foundation code actually changed (new foundation selected)
    // Don't reset if just layout is being edited
    const foundationCodeChanged = lastFoundationCodeRef.current !== foundation?.code;

    if (foundationCodeChanged && piles && piles.length > 0) {
      const maxId = piles.length;
      const defaultDeltaZ = foundation?.pile_length || 12.0; // ΔZ = distance from slab bottom to pile tip
      const initialPileData = piles.map((pile, index) => ({
        id: index + 1,
        x: pile[0],
        y: pile[1], // Y+ is upwards (standard Cartesian coordinates)
        angle_x: Array.isArray(pile) && pile.length >= 5 ? (pile[2] || 0) : 0, // Angle in X-Z plane (degrees)
        angle_y: Array.isArray(pile) && pile.length >= 5 ? (pile[3] || 0) : 0, // Angle in Y-Z plane (degrees)
        length: Array.isArray(pile) && pile.length >= 5 ? (pile[4] || defaultDeltaZ) : (pile.length >= 4 ? pile[3] : (pile.length > 2 ? pile[2] : defaultDeltaZ)), // ΔZ: distance from slab bottom to pile tip
        type: foundation?.pile_type || 'RTB',
        size: foundation?.pile_size || 300,
        originalX: pile[0],
        originalY: pile[1],
        originalAngleX: Array.isArray(pile) && pile.length >= 5 ? (pile[2] || 0) : 0,
        originalAngleY: Array.isArray(pile) && pile.length >= 5 ? (pile[3] || 0) : 0,
        originalLength: Array.isArray(pile) && pile.length >= 5 ? (pile[4] || defaultDeltaZ) : (pile.length >= 4 ? pile[3] : (pile.length > 2 ? pile[2] : defaultDeltaZ))
      }));
      setPileData(initialPileData);
      setNextPileId(maxId + 1);
      setValidationErrors([]); // Clear validation errors when foundation changes
      setInputValues({}); // Clear input values when foundation changes
      initializedRef.current = true;
      lastFoundationCodeRef.current = foundation?.code;
    } else if (!initializedRef.current && piles && piles.length > 0) {
      // Initial load - initialize pile data
      const maxId = piles.length;
      const defaultPileLength = foundation?.pile_length || 12.0;
      const initialPileData = piles.map((pile, index) => ({
        id: index + 1,
        x: pile[0],
        y: pile[1],
        type: foundation?.pile_type || 'RTB',
        size: foundation?.pile_size || 300,
        length: Array.isArray(pile) && pile.length > 2 ? pile[2] : defaultPileLength,
        originalX: pile[0],
        originalY: pile[1],
        originalLength: Array.isArray(pile) && pile.length > 2 ? pile[2] : defaultPileLength
      }));
      setPileData(initialPileData);
      setNextPileId(maxId + 1);
      setInputValues({}); // Clear input values on initial load
      initializedRef.current = true;
      lastFoundationCodeRef.current = foundation?.code;
    }
  }, [foundation?.code]); // Only depend on foundation code, not piles or layout

  // Early return if no foundation data (AFTER all hooks)
  if (!foundation || piles.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography color="text.secondary">
            No foundation layout available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Add a new pile at specified position
  const addPile = (x, y, deltaZ = null) => {
    const defaultDeltaZ = foundation?.pile_length || 12.0;
    const newPile = {
      id: nextPileId,
      x: x,
      y: y,
      angle_x: 0, // New piles start vertical
      angle_y: 0, // New piles start vertical
      type: foundation.pile_type || 'RTB',
      size: foundation.pile_size || 300,
      length: deltaZ !== null ? deltaZ : defaultDeltaZ,
      originalX: x,
      originalY: y,
      originalAngleX: 0,
      originalAngleY: 0,
      originalLength: deltaZ !== null ? deltaZ : defaultDeltaZ
    };

    const updatedPileData = [...pileData, newPile];
    setPileData(updatedPileData);
    setNextPileId(nextPileId + 1);

    // Update parent component
    if (onLayoutChange) {
      // Include angles and deltaZ in layout: [x, y, angle_x, angle_y, deltaZ]
      const newLayout = updatedPileData.map(pile => [pile.x, pile.y, pile.angle_x || 0, pile.angle_y || 0, pile.length || defaultDeltaZ]);
      onLayoutChange({
        ...foundation,
        layout: newLayout,
        pileData: updatedPileData,
        pile_lengths: updatedPileData.map(pile => pile.length || defaultDeltaZ)
      });
    }
  };

  // Calculate 3D pile position and orientation
  const getPileGeometry = (pile) => {
    const angleXRad = (pile.angle_x || 0) * Math.PI / 180;
    const angleYRad = (pile.angle_y || 0) * Math.PI / 180;
    const deltaZ = pile.length || foundation?.pile_length || 12.0;
    const pileRadius = PILE_SIZE / 2;

    // For 3D diagonal piles, the tip coordinates are calculated using both angles
    // The pile direction is determined by both angle_x and angle_y
    const horizontalOffsetX = Math.tan(angleXRad) * deltaZ;
    const horizontalOffsetY = Math.tan(angleYRad) * deltaZ;

    // Top point (at foundation level)
    const top = { x: pile.x, y: pile.y, z: 0 };

    // Bottom point (at tip) - 3D offset
    const bottom = {
      x: pile.x + horizontalOffsetX,
      y: pile.y + horizontalOffsetY,
      z: -deltaZ
    };

    // Direction vector
    const direction = {
      x: bottom.x - top.x,
      y: bottom.y - top.y,
      z: bottom.z - top.z
    };

    // Normalize direction
    const length3D = Math.sqrt(direction.x**2 + direction.y**2 + direction.z**2);
    const unitDir = {
      x: direction.x / length3D,
      y: direction.y / length3D,
      z: direction.z / length3D
    };

    return { top, bottom, direction: unitDir, radius: pileRadius, length: length3D };
  };

  // Check 3D collision between two piles
  const checkPileCollision3D = (pile1, pile2) => {
    const geom1 = getPileGeometry(pile1);
    const geom2 = getPileGeometry(pile2);

    // Simple cylinder-cylinder intersection check
    // For now, use distance between centers along piles
    const dx = geom2.top.x - geom1.top.x;
    const dy = geom2.top.y - geom1.top.y;
    const dz = geom2.top.z - geom1.top.z;

    // Project onto plane perpendicular to average direction
    const avgDir = {
      x: (geom1.direction.x + geom2.direction.x) / 2,
      y: (geom1.direction.y + geom2.direction.y) / 2,
      z: (geom1.direction.z + geom2.direction.z) / 2
    };

    // Distance between pile axes
    const dist = Math.sqrt(dx**2 + dy**2 + dz**2);
    const minSeparation = geom1.radius + geom2.radius;

    return dist < minSeparation;
  };

  // Check if pile tip would extend below bedrock (informational — backend clips at bedrock)
  const checkBedrockCollision = (angleX, angleY, deltaZ, x, y, excludeId = null) => {
    const warnings = [];
    const isDiagonal = angleX !== 0 || angleY !== 0;

    const bedrockLayer = foundation?.soil_layers?.find((layer) => {
      const name = (layer.soil_type || layer.name || '').toLowerCase();
      const eRaw = parseFloat(layer.E_modulus ?? layer.modulus ?? 0);
      const eMpa = eRaw > 500 ? eRaw / 1000 : eRaw;
      return name.includes('bedrock') || (name.includes('rock') && eMpa >= 500) || eMpa >= 500;
    });

    if (bedrockLayer) {
      const bedrockTop = bedrockLayer.top_depth ?? 0;
      const embedment = foundation?.embedment_depth ?? 0;
      const capH = foundation?.dimensions?.H ?? 0;
      const tipDepth = embedment + capH + deltaZ;

      if (tipDepth > bedrockTop + 0.01) {
        const pileType = isDiagonal ? 'diagonal' : 'vertical';
        warnings.push({
          type: 'warning',
          message: `${pileType} pile tip at ${tipDepth.toFixed(1)} m exceeds bedrock top at ${bedrockTop.toFixed(1)} m — analysis clips piles at bedrock`,
        });
      }
    }

    return warnings;
  };

  // Check if piles are overlapping or too close
  const checkPileSeparation = (x, y, excludeId = null) => {
    const warnings = [];

    // Check 3D collisions between all piles
    for (let i = 0; i < pileData.length; i++) {
      for (let j = i + 1; j < pileData.length; j++) {
        const pile1 = pileData[i];
        const pile2 = pileData[j];

        if ((excludeId && (pile1.id === excludeId || pile2.id === excludeId))) continue;

        if (checkPileCollision3D(pile1, pile2)) {
          warnings.push({
            type: 'error',
            message: `Pile ${pile1.id} collides with Pile ${pile2.id} in 3D space`
          });
        }
      }
    }

    // Check proximity for the pile being positioned
    for (const pile of pileData) {
      if (excludeId && pile.id === excludeId) continue;

      const distance = Math.sqrt((pile.x - x) ** 2 + (pile.y - y) ** 2);
      if (distance < MIN_PILE_SEPARATION) {
        if (distance < PILE_SIZE) {
          warnings.push({
            type: 'error',
            message: `Pile overlaps with Pile ${pile.id} (distance: ${(distance * 1000).toFixed(0)}mm < pile size ${(PILE_SIZE * 1000).toFixed(0)}mm)`
          });
        } else {
          warnings.push({
            type: 'warning',
            message: `Pile too close to Pile ${pile.id} (distance: ${(distance * 1000).toFixed(0)}mm < minimum ${(MIN_PILE_SEPARATION * 1000).toFixed(0)}mm)`
          });
        }
      }
    }
    return warnings;
  };

  // Add pile in smart location based on existing pile pattern
  const addPileBelow = () => {
    if (pileData.length === 0) {
      // If no piles, add at origin
      addPile(0, 0);
      return;
    }

    const numPiles = pileData.length;
    const defaultPileLength = foundation?.pile_length || 12.0;
    
    // Calculate pile positions based on pattern
    let newX = 0;
    let newY = 0;
    
    if (numPiles === 1) {
      // One pile exists - add second at spacing distance
      const spacing = foundation?.spacing || 1.0;
      newX = pileData[0].x + spacing;
      newY = pileData[0].y;
    } else if (numPiles === 2) {
      // Two piles - add third to form triangle
      const spacing = foundation?.spacing || 1.0;
      const midX = (pileData[0].x + pileData[1].x) / 2;
      const midY = (pileData[0].y + pileData[1].y) / 2;
      // Add perpendicular to the line between two piles
      const dx = pileData[1].x - pileData[0].x;
      const dy = pileData[1].y - pileData[0].y;
      const perpX = -dy;
      const perpY = dx;
      const perpLength = Math.sqrt(perpX ** 2 + perpY ** 2);
      if (perpLength > 0) {
        newX = midX + (perpX / perpLength) * spacing;
        newY = midY + (perpY / perpLength) * spacing;
      } else {
        newX = midX;
        newY = midY + spacing;
      }
    } else if (numPiles === 3) {
      // Three piles - add fourth to complete square/rectangle
      // Find bounding box
      const xs = pileData.map(p => p.x);
      const ys = pileData.map(p => p.y);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      
      // Find missing corner
      const corners = [
        [minX, minY],
        [minX, maxY],
        [maxX, minY],
        [maxX, maxY]
      ];
      
      for (const [cx, cy] of corners) {
        const exists = pileData.some(p => Math.abs(p.x - cx) < 0.01 && Math.abs(p.y - cy) < 0.01);
        if (!exists) {
          newX = cx;
          newY = cy;
          break;
        }
      }
      
      // If all corners filled, add at center
      if (newX === 0 && newY === 0) {
        newX = (minX + maxX) / 2;
        newY = (minY + maxY) / 2;
      }
    } else if (numPiles === 4) {
      // Four piles - add fifth at center (0,0) or between existing piles
      // Check if center is empty
      const centerExists = pileData.some(p => Math.abs(p.x) < 0.01 && Math.abs(p.y) < 0.01);
      if (!centerExists) {
        newX = 0;
        newY = 0;
      } else {
        // Find positions: -x_max,0 then x_max,0 then 0,y_max then 0,-y_max
        const xs = pileData.map(p => p.x);
        const ys = pileData.map(p => p.y);
        const maxX = Math.max(...xs.map(Math.abs));
        const maxY = Math.max(...ys.map(Math.abs));
        
        const positions = [
          [-maxX, 0],
          [maxX, 0],
          [0, maxY],
          [0, -maxY]
        ];
        
        for (const [px, py] of positions) {
          const exists = pileData.some(p => Math.abs(p.x - px) < 0.01 && Math.abs(p.y - py) < 0.01);
          if (!exists) {
            newX = px;
            newY = py;
            break;
          }
        }
        
        // If all positions filled, add at midpoint
        if (newX === 0 && newY === 0) {
          newX = maxX / 2;
          newY = 0;
        }
      }
    } else if (numPiles === 5) {
      // Five piles - add sixth at center (0,0) if not already there
      const centerExists = pileData.some(p => Math.abs(p.x) < 0.01 && Math.abs(p.y) < 0.01);
      if (!centerExists) {
        newX = 0;
        newY = 0;
      } else {
        // Add at a strategic position
        const xs = pileData.map(p => p.x);
        const ys = pileData.map(p => p.y);
        const maxX = Math.max(...xs.map(Math.abs));
        const maxY = Math.max(...ys.map(Math.abs));
        newX = maxX / 2;
        newY = maxY / 2;
      }
    } else {
      // Six or more piles - add at center (0,0) if not already there
      const centerExists = pileData.some(p => Math.abs(p.x) < 0.01 && Math.abs(p.y) < 0.01);
      if (!centerExists) {
        newX = 0;
        newY = 0;
      } else {
        // Add at spacing from last pile
        const lastPile = pileData[pileData.length - 1];
        const spacing = foundation?.spacing || 1.0;
        newX = lastPile.x + spacing;
        newY = lastPile.y;
      }
    }
    
    // Check for separation issues (new piles start as vertical)
    const separationWarnings = checkPileSeparation(newX, newY);
    const bedrockWarnings = checkBedrockCollision(0, 0, foundation?.pile_length || 12.0, newX, newY);
    if (separationWarnings.length > 0 || bedrockWarnings.length > 0) {
      setValidationErrors(prev => [...prev, ...separationWarnings, ...bedrockWarnings]);
    }

    addPile(newX, newY);
  };

  // Calculate foundation bounds (including minimum edge distance from pile outer edge)
  const foundationHalfWidth = dimensions.W / 2;
  const foundationHalfLength = dimensions.L / 2;
  // Effective area accounts for pile radius + minimum edge distance
  const effectiveHalfWidth = foundationHalfWidth - PILE_RADIUS - MIN_EDGE_DISTANCE;
  const effectiveHalfLength = foundationHalfLength - PILE_RADIUS - MIN_EDGE_DISTANCE;

  // Calculate bounds for visualization (Y+ upwards, so we flip the Y coordinates for SVG)
  const xCoords = piles.map(p => p[0]);
  const yCoords = piles.map(p => p[1]); // Y coordinates are already in standard Cartesian (Y+ up)

  // Include vector endpoints for diagonal piles
  const vectorCoords = piles
    .filter(p => Math.abs(p.angle_x || 0) > 0.1 || Math.abs(p.angle_y || 0) > 0.1)
    .map(p => {
      const deltaZ = p.length || foundation?.pile_length || 12.0;
      const deltaX = deltaZ * Math.tan((p.angle_x || 0) * Math.PI / 180);
      const deltaY = deltaZ * Math.tan((p.angle_y || 0) * Math.PI / 180);
      return {
        x: p[0] + deltaX * 0.3, // Include scaled vector endpoint
        y: p[1] + deltaY * 0.3
      };
    });

  const allXCoords = [...xCoords, ...vectorCoords.map(v => v.x)];
  const allYCoords = [...yCoords, ...vectorCoords.map(v => v.y)];

  const minX = Math.min(...allXCoords);
  const maxX = Math.max(...allXCoords);
  const minY = Math.min(...allYCoords);
  const maxY = Math.max(...allYCoords);

  // Include foundation outline in bounds
  const foundationMinX = -foundationHalfLength;
  const foundationMaxX = foundationHalfLength;
  const foundationMinY = -foundationHalfWidth; // Y+ up, so negative Y is down
  const foundationMaxY = foundationHalfWidth;  // Y+ up, so positive Y is up

  const overallMinX = Math.min(minX, foundationMinX);
  const overallMaxX = Math.max(maxX, foundationMaxX);
  const overallMinY = Math.min(minY, foundationMinY);
  const overallMaxY = Math.max(maxY, foundationMaxY);

  const rangeX = overallMaxX - overallMinX || 1;
  const rangeY = overallMaxY - overallMinY || 1;
  const padding = Math.max(rangeX, rangeY) * 0.15;

  const viewBoxWidth = rangeX + 2 * padding;
  const viewBoxHeight = rangeY + 2 * padding;
  const viewBoxX = overallMinX - padding;
  const viewBoxY = -overallMaxY - padding; // Flip Y: overallMaxY becomes the top (lowest SVG Y)

  // Scale factor for SVG to fit in container
  const svgWidth = 400;
  const svgHeight = 300;
  const scaleFactor = Math.min(svgWidth / viewBoxWidth, svgHeight / viewBoxHeight);

  const getPileTypeColor = (pileType) => {
    if (pileType === 'RTC') return '#2196f3'; // Blue for RTC
    if (pileType === 'RTB') return '#4caf50'; // Green for RTB
    return '#ff9800'; // Orange for others
  };

  // Helper to convert standard Cartesian Y to SVG Y
  const toSvgY = (y) => -y;

  // Validate pile position
  const validatePilePosition = (x, y) => {
    const distanceFromLeft = x - (-effectiveHalfLength);
    const distanceFromRight = effectiveHalfLength - x;
    const distanceFromBottom = y - (-effectiveHalfWidth);
    const distanceFromTop = effectiveHalfWidth - y;

    const minDistance = Math.min(distanceFromLeft, distanceFromRight, distanceFromBottom, distanceFromTop);

    return {
      isValid: minDistance >= 0,
      minDistance: minDistance,
      correctedX: Math.max(-effectiveHalfLength, Math.min(effectiveHalfLength, x)),
      correctedY: Math.max(-effectiveHalfWidth, Math.min(effectiveHalfWidth, y))
    };
  };

  // Update pile data
  const updatePileData = (pileId, field, value) => {
    // Prevent resetting during updates
    initializedRef.current = true;
    
    const updatedPileData = pileData.map(pile => {
      if (pile.id === pileId) {
        const updatedPile = { ...pile, [field]: value };

        // Validate position if coordinates changed
        if (field === 'x' || field === 'y') {
          const validation = validatePilePosition(updatedPile.x, updatedPile.y);

          if (!validation.isValid) {
            // Auto-correct to edge position
            updatedPile.x = validation.correctedX;
            updatedPile.y = validation.correctedY;

            // Add validation error
            setValidationErrors(prev => {
              const existing = prev.filter(err => err.pileId !== pileId);
              return [...existing, {
                pileId,
                type: 'error',
                message: `Pile ${pileId} moved to foundation edge (minimum ${MIN_EDGE_DISTANCE * 1000}mm from edge required)`
              }];
            });
          } else {
            // Check for pile separation issues
            const separationWarnings = checkPileSeparation(updatedPile.x, updatedPile.y, pileId);
            const bedrockWarnings = checkBedrockCollision(updatedPile.angle_x, updatedPile.angle_y, updatedPile.length, updatedPile.x, updatedPile.y, pileId);
            
            // Remove old errors for this pile, then add warnings
            setValidationErrors(prev => {
              let filtered = prev.filter(err => err.pileId !== pileId);
              // Add separation warnings
              if (separationWarnings.length > 0) {
                filtered = [...filtered, ...separationWarnings.map(w => ({ ...w, pileId }))];
              }
              // Add bedrock warnings
              if (bedrockWarnings.length > 0) {
                filtered = [...filtered, ...bedrockWarnings.map(w => ({ ...w, pileId }))];
              }
              return filtered;
            });
          }
        }

        // Validate length if changed
        if (field === 'length') {
          if (value < 3 || value > 50) {
            setValidationErrors(prev => {
              const existing = prev.filter(err => err.pileId !== pileId);
              return [...existing, {
                pileId,
                type: 'error',
                message: `Pile ${pileId} length must be between 3m and 50m`
              }];
            });
          } else {
            setValidationErrors(prev => prev.filter(err => err.pileId !== pileId));
          }
        }

        return updatedPile;
      }
      return pile;
    });

    setPileData(updatedPileData);

    // Update parent component if callback provided - this should trigger custom mode
    if (onLayoutChange) {
      // Include length in layout: [x, y, length]
      const newLayout = updatedPileData.map(pile => [pile.x, pile.y, pile.length || foundation?.pile_length || 12.0]);
      onLayoutChange({ 
        ...foundation, 
        layout: newLayout, 
        pileData: updatedPileData,
        pile_lengths: updatedPileData.map(p => p.length || foundation?.pile_length || 12.0), // Array of lengths
        isCustom: true // Mark as custom when layout changes
      });
    }
  };

  return (
    <Card>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <Visibility />
            <Typography variant="h6">
              {foundation?.customName || foundation?.code || 'Foundation Layout'}
            </Typography>
            <Chip
              label={`${numPiles} piles`}
              size="small"
              color="primary"
              variant="outlined"
            />
            {foundation?.isCustom && (
              <Chip
                label="Custom"
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
          </Box>
        }
        action={
          <IconButton onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        }
      />
      <CardContent>
        {/* Summary Info */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} md={3}>
            <Typography variant="body2" color="text.secondary">Type</Typography>
            <Typography variant="body1">{foundation.code || 'Custom'}</Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="body2" color="text.secondary">Piles</Typography>
            <Typography variant="body1">{numPiles} piles</Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="body2" color="text.secondary">Dimensions</Typography>
            <Typography variant="body1">{dimensions.L}×{dimensions.W}×{dimensions.H}m</Typography>
          </Grid>
          <Grid item xs={6} md={3}>
            <Typography variant="body2" color="text.secondary">Foundation</Typography>
            <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
              {foundation.foundation_type || 'unknown'}
            </Typography>
          </Grid>
        </Grid>

        {/* Validation Errors and Warnings */}
        {validationErrors.length > 0 && (
          <Box sx={{ mb: 2 }}>
            {validationErrors.map((error, index) => (
              <Alert 
                key={index} 
                severity={error.type === 'error' ? 'error' : 'warning'} 
                sx={{ mb: 1 }}
              >
                <Typography variant="body2">
                  {error.pileId ? `Pile ${error.pileId}: ` : ''}{error.message}
                </Typography>
              </Alert>
            ))}
          </Box>
        )}

        {/* 2D Visualization */}
        <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationOn fontSize="small" />
            Pile Layout (Top View)
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <svg
              width={svgWidth}
              height={svgHeight}
              viewBox={`${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}`}
              style={{
                border: '1px solid #ddd',
                borderRadius: '4px',
                // Reset inherited typography (e.g. MUI / theme letter-spacing in em) — otherwise
                // SVG <text> in userSpaceOnUse units can render with huge gaps between glyphs.
                letterSpacing: 0,
                wordSpacing: 0,
                fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
              }}
            >
              {/* Background grid */}
              <defs>
                <pattern id="grid" width="0.5" height="0.5" patternUnits="userSpaceOnUse">
                  <path d="M 0.5 0 L 0 0 0 0.5" fill="none" stroke="#e0e0e0" strokeWidth="0.01"/>
                </pattern>
                <marker id="arrowheadOrange" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
                  <polygon points="0 0, 6 2, 0 4" fill="#ff6b35" />
                </marker>
              </defs>
              <rect
                x={viewBoxX}
                y={viewBoxY}
                width={viewBoxWidth}
                height={viewBoxHeight}
                fill="url(#grid)"
              />

              {/* Foundation effective area (minimum edge distance zone) */}
              {dimensions.L > 0 && dimensions.W > 0 && (
                <rect
                  x={-effectiveHalfLength}
                  y={toSvgY(effectiveHalfWidth)}
                  width={effectiveHalfLength * 2}
                  height={effectiveHalfWidth * 2}
                  fill="#e8f5e8"
                  stroke="#4caf50"
                  strokeWidth="0.02"
                  opacity="0.3"
                />
              )}

              {/* Foundation outline */}
              {dimensions.L > 0 && dimensions.W > 0 && (
                <rect
                  x={-foundationHalfLength}
                  y={toSvgY(foundationHalfWidth)}
                  width={foundationHalfLength * 2}
                  height={foundationHalfWidth * 2}
                  fill="none"
                  stroke="#666"
                  strokeWidth="0.03"
                />
              )}

              {/* Coordinate axes labels */}
              <text
                x={viewBoxX + viewBoxWidth - 0.2}
                y={toSvgY(0.2)}
                textAnchor="end"
                fontSize="0.15"
                fill="#666"
                style={{ letterSpacing: 0, wordSpacing: 0 }}
              >
                X
              </text>
              <text
                x="-0.2"
                y={viewBoxY + 0.2}
                textAnchor="middle"
                fontSize="0.15"
                fill="#666"
                style={{ letterSpacing: 0, wordSpacing: 0 }}
              >
                Y
              </text>

              {/* Pile positions as squares */}
              {pileData.map((pile) => (
                <g key={pile.id}>
                  {/* Pile square */}
                  <rect
                    x={pile.x - PILE_RADIUS}
                    y={toSvgY(pile.y + PILE_RADIUS)}
                    width={PILE_SIZE}
                    height={PILE_SIZE}
                    fill={getPileTypeColor(pile.type)}
                    stroke="#000"
                    strokeWidth="0.02"
                    rx="0.02"
                  />
                  {/* Pile number */}
                  <text
                    x={pile.x}
                    y={toSvgY(pile.y)}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="0.15"
                    fill="white"
                    fontWeight="bold"
                    style={{ letterSpacing: 0, wordSpacing: 0 }}
                  >
                    {pile.id}
                  </text>
                  {/* Pile type and size label (single string — avoids spaced-out glyph layout) */}
                  <text
                    x={pile.x}
                    y={toSvgY(pile.y - PILE_RADIUS - 0.1)}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="0.12"
                    fill="#333"
                    style={{ letterSpacing: 0, wordSpacing: 0 }}
                  >
                    {`${pile.type || ''}${pile.size != null ? pile.size : ''}`}
                  </text>

                  {/* Diagonal pile vector (projected direction) */}
                  {(Math.abs(pile.angle_x) > 0.1 || Math.abs(pile.angle_y) > 0.1) && (
                    <>
                      {/* Calculate vector endpoint */}
                      {(() => {
                        const deltaZ = pile.length || foundation?.pile_length || 12.0;

                        // Calculate actual horizontal displacements based on individual angles
                        const deltaX = deltaZ * Math.tan(pile.angle_x * Math.PI / 180);
                        const deltaY = deltaZ * Math.tan(pile.angle_y * Math.PI / 180);

                        // Vector endpoint (scaled for visibility)
                        const scaleFactor = 0.3; // Show 30% of actual projection
                        const endX = pile.x + deltaX * scaleFactor;
                        const endY = pile.y + deltaY * scaleFactor;

                        // Only draw vector if it has meaningful length
                        const actualVectorLength = Math.sqrt(deltaX**2 + deltaY**2);
                        if (actualVectorLength < 0.01) return null;

                        return (
                          <>
                            {/* Vector line */}
                            <line
                              x1={pile.x}
                              y1={toSvgY(pile.y)}
                              x2={endX}
                              y2={toSvgY(endY)}
                              stroke="#ff6b35"
                              strokeWidth="0.03"
                              strokeDasharray="0.05 0.05"
                              opacity="0.8"
                              markerEnd="url(#arrowheadOrange)"
                            />
                            {/* Vector label */}
                            <text
                              x={(pile.x + endX) / 2}
                              y={toSvgY((pile.y + endY) / 2 - 0.1)}
                              textAnchor="middle"
                              fontSize="0.08"
                              fill="#ff6b35"
                              fontWeight="bold"
                              style={{ letterSpacing: 0, wordSpacing: 0 }}
                            >
                              {pile.angle_x !== 0 && pile.angle_y !== 0 ?
                                `${pile.angle_x}°,${pile.angle_y}°` :
                                pile.angle_x !== 0 ? `${pile.angle_x}°` : `${pile.angle_y}°`}
                            </text>
                          </>
                        );
                      })()}
                    </>
                  )}
                </g>
              ))}

              {/* Coordinate axes */}
              <line x1={viewBoxX} y1="0" x2={viewBoxX + viewBoxWidth} y2="0" stroke="#999" strokeWidth="0.02" opacity="0.7"/>
              <line x1="0" y1={viewBoxY} x2="0" y2={viewBoxY + viewBoxHeight} stroke="#999" strokeWidth="0.02" opacity="0.7"/>
            </svg>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
            Green zone: Valid pile placement area (≥{MIN_EDGE_DISTANCE * 1000}mm from pile outer edge to foundation edge). Origin (0,0) is foundation center. Y+ upwards.
          </Typography>
        </Box>

        {/* Collapsible Editable Table */}
        <Collapse in={expanded}>
          <TableContainer component={Paper} variant="outlined">
            <Box sx={{ p: 2, pb: 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TableChart fontSize="small" />
                  Pile Configuration Table {isEditable && '(Editable)'}
                </Typography>
              </Box>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Pile #</strong></TableCell>
                  <TableCell><strong>Type & Size</strong></TableCell>
                  <TableCell align="right"><strong>X (m)</strong></TableCell>
                  <TableCell align="right"><strong>Y (m)</strong></TableCell>
                  <TableCell align="right"><strong>Angle X (°)</strong></TableCell>
                  <TableCell align="right"><strong>Angle Y (°)</strong></TableCell>
                  <TableCell align="right"><strong>ΔZ (m)</strong></TableCell>
                  <TableCell align="center"><strong>Status</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pileData.map((pile) => {
                  const validation = validatePilePosition(pile.x, pile.y);
                  const hasError = pile.x !== pile.originalX || pile.y !== pile.originalY;

                  return (
                    <TableRow key={pile.id} sx={{ backgroundColor: hasError ? '#fff3e0' : 'inherit' }}>
                      <TableCell>{pile.id}</TableCell>
                      <TableCell>
                        {isEditable ? (
                          <Select
                            value={`${pile.type} ${pile.size}`}
                            onChange={(e) => {
                              const [type, sizeStr] = e.target.value.split(' ');
                              const size = parseInt(sizeStr);
                              updatePileData(pile.id, 'type', type);
                              updatePileData(pile.id, 'size', size);
                            }}
                            size="small"
                            sx={{ minWidth: 100 }}
                          >
                            <MenuItem value="RTB 250">RTB 250</MenuItem>
                            <MenuItem value="RTB 300">RTB 300</MenuItem>
                            <MenuItem value="RTC 300">RTC 300</MenuItem>
                            <MenuItem value="RTC 350">RTC 350</MenuItem>
                          </Select>
                        ) : (
                          `${pile.type} ${pile.size}`
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isEditable ? (
                          <TextField
                            type="text"
                            value={inputValues[`x_${pile.id}`] !== undefined ? inputValues[`x_${pile.id}`] : (pile.x === 0 ? '0' : pile.x.toString())}
                            onChange={(e) => {
                              const inputVal = e.target.value;
                              // Allow numeric entry characters (including comma)
                              if (inputVal === '' || inputVal === '-' || inputVal === '-.' || inputVal === '-,' || inputVal === '.' || inputVal === ',' || /^-?\d*[.,]?\d*$/.test(inputVal)) {
                                setInputValues(prev => ({ ...prev, [`x_${pile.id}`]: inputVal }));
                              }
                            }}
                            onBlur={(e) => {
                              const inputVal = e.target.value.replace(',', '.');
                              const val = parseFloat(inputVal);
                              if (!isNaN(val)) {
                                updatePileData(pile.id, 'x', val);
                              } else {
                                // Revert to 0 if invalid
                                updatePileData(pile.id, 'x', 0);
                              }
                              // Clear local input state to sync with pileData
                              setInputValues(prev => {
                                const next = { ...prev };
                                delete next[`x_${pile.id}`];
                                return next;
                              });
                            }}
                            size="small"
                            inputProps={{
                              style: { textAlign: 'right', width: '80px' }
                            }}
                            error={!validation.isValid}
                          />
                        ) : (
                          pile.x.toFixed(3)
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isEditable ? (
                          <TextField
                            type="text"
                            value={inputValues[`y_${pile.id}`] !== undefined ? inputValues[`y_${pile.id}`] : (pile.y === 0 ? '0' : pile.y.toString())}
                            onChange={(e) => {
                              const inputVal = e.target.value;
                              if (inputVal === '' || inputVal === '-' || inputVal === '-.' || inputVal === '-,' || inputVal === '.' || inputVal === ',' || /^-?\d*[.,]?\d*$/.test(inputVal)) {
                                setInputValues(prev => ({ ...prev, [`y_${pile.id}`]: inputVal }));
                              }
                            }}
                            onBlur={(e) => {
                              const inputVal = e.target.value.replace(',', '.');
                              const val = parseFloat(inputVal);
                              if (!isNaN(val)) {
                                updatePileData(pile.id, 'y', val);
                              } else {
                                updatePileData(pile.id, 'y', 0);
                              }
                              setInputValues(prev => {
                                const next = { ...prev };
                                delete next[`y_${pile.id}`];
                                return next;
                              });
                            }}
                            size="small"
                            inputProps={{
                              style: { textAlign: 'right', width: '80px' }
                            }}
                            error={!validation.isValid}
                          />
                        ) : (
                          pile.y.toFixed(3)
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isEditable ? (
                          <TextField
                            type="text"
                            size="small"
                            value={inputValues[`angle_x_${pile.id}`] !== undefined ? inputValues[`angle_x_${pile.id}`] : (pile.angle_x || 0).toString()}
                            onChange={(e) => {
                              const inputVal = e.target.value;
                              // Allow numeric entry characters (including comma and negative)
                              if (inputVal === '' || inputVal === '-' || inputVal === '-.' || inputVal === '-,' || inputVal === '.' || inputVal === ',' || /^-?\d*[.,]?\d*$/.test(inputVal)) {
                                setInputValues(prev => ({ ...prev, [`angle_x_${pile.id}`]: inputVal }));
                              }
                            }}
                            onBlur={(e) => {
                              const inputVal = e.target.value.replace(',', '.');
                              const val = parseFloat(inputVal);
                              if (!isNaN(val)) {
                                // Clamp angle to ±45° range
                                const clampedAngle = Math.max(-45, Math.min(45, val));
                                updatePileData(pile.id, 'angle_x', clampedAngle);
                              } else {
                                // Revert to 0 if invalid
                                updatePileData(pile.id, 'angle_x', 0);
                              }
                              setInputValues(prev => {
                                const next = { ...prev };
                                delete next[`angle_x_${pile.id}`];
                                return next;
                              });
                            }}
                            inputProps={{
                              style: { textAlign: 'right', fontSize: '0.875rem' }
                            }}
                            sx={{ width: 80 }}
                          />
                        ) : (
                          (pile.angle_x || 0).toFixed(1)
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isEditable ? (
                          <TextField
                            type="text"
                            size="small"
                            value={inputValues[`angle_y_${pile.id}`] !== undefined ? inputValues[`angle_y_${pile.id}`] : (pile.angle_y || 0).toString()}
                            onChange={(e) => {
                              const inputVal = e.target.value;
                              // Allow numeric entry characters (including comma and negative)
                              if (inputVal === '' || inputVal === '-' || inputVal === '-.' || inputVal === '-,' || inputVal === '.' || inputVal === ',' || /^-?\d*[.,]?\d*$/.test(inputVal)) {
                                setInputValues(prev => ({ ...prev, [`angle_y_${pile.id}`]: inputVal }));
                              }
                            }}
                            onBlur={(e) => {
                              const inputVal = e.target.value.replace(',', '.');
                              const val = parseFloat(inputVal);
                              if (!isNaN(val)) {
                                // Clamp angle to ±45° range
                                const clampedAngle = Math.max(-45, Math.min(45, val));
                                updatePileData(pile.id, 'angle_y', clampedAngle);
                              } else {
                                // Revert to 0 if invalid
                                updatePileData(pile.id, 'angle_y', 0);
                              }
                              setInputValues(prev => {
                                const next = { ...prev };
                                delete next[`angle_y_${pile.id}`];
                                return next;
                              });
                            }}
                            inputProps={{
                              style: { textAlign: 'right', fontSize: '0.875rem' }
                            }}
                            sx={{ width: 80 }}
                          />
                        ) : (
                          (pile.angle_y || 0).toFixed(1)
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isEditable ? (
                          <TextField
                            type="text"
                            value={inputValues[`length_${pile.id}`] !== undefined ? inputValues[`length_${pile.id}`] : (pile.length || foundation?.pile_length || 12.0).toString()}
                            onChange={(e) => {
                              const inputVal = e.target.value;
                              if (inputVal === '' || inputVal === '.' || inputVal === ',' || /^\d*[.,]?\d*$/.test(inputVal)) {
                                setInputValues(prev => ({ ...prev, [`length_${pile.id}`]: inputVal }));
                              }
                            }}
                            onBlur={(e) => {
                              const inputVal = e.target.value.replace(',', '.');
                              const val = parseFloat(inputVal);
                              const defaultDeltaZ = foundation?.pile_length || 12.0;
                              if (!isNaN(val)) {
                                updatePileData(pile.id, 'length', val);
                              } else {
                                updatePileData(pile.id, 'length', defaultDeltaZ);
                              }
                              setInputValues(prev => {
                                const next = { ...prev };
                                delete next[`length_${pile.id}`];
                                return next;
                              });
                            }}
                            size="small"
                            inputProps={{
                              style: { textAlign: 'right', width: '70px' }
                            }}
                            error={pile.length < 3 || pile.length > 50}
                          />
                        ) : (
                          (pile.length || foundation?.pile_length || 12.0).toFixed(2)
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {validation.isValid && (!pile.length || (pile.length >= 3 && pile.length <= 50)) ? (
                          <Chip label="OK" size="small" color="success" variant="outlined" />
                        ) : (
                          <Chip label={validation.isValid ? "LENGTH" : "EDGE"} size="small" color="warning" variant="outlined" />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {isEditable && (
              <Box sx={{ p: 1, borderTop: '1px solid #e0e0e0' }}>
                <Button 
                  size="small" 
                  variant="outlined" 
                  onClick={addPileBelow}
                  fullWidth
                >
                  Add Pile
                </Button>
              </Box>
            )}
          </TableContainer>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default FoundationLayoutViewer;
